import { useState } from "react";
import { PERSON_REPORT_CATEGORY_LABEL, PersonReportCategory, isAiResidentActorId } from "@townsy/contracts";
import { errorMessage } from "../../api/client";
import { postPersonReport } from "../../api/safety";
import { formatDateTime } from "../../lib/format";
import { useTownStore, type PersonReportRequestUi } from "../../state/store";
import { Dialog } from "../Dialog";
import { ReportFlagIcon } from "./ReportFlagIcon";

/**
 * The confirmation after a report (the reporter is never revealed to the person). `messageIncluded` is the server's
 * answer for "Report this message": whether that exact message is in the report.
 */
export function reportSentText(label: string, duplicate: boolean, messageIncluded?: boolean): string {
  if (duplicate) {
    return messageIncluded
      ? `You already reported ${label} a moment ago. This message was added to that report.`
      : `You already reported ${label} a moment ago. A Townsy moderator will look at it.`;
  }
  const base = `Thanks. A Townsy moderator will look at your report. ${label} is not told who reported them.`;
  return messageIncluded === false ? `${base} That message was no longer available, so their recent messages were sent instead.` : base;
}

/** The dialog's opening line: what the moderator will see, and that the person is not told who reported them. */
export function reportIntroText(req: Pick<PersonReportRequestUi, "label" | "conversationId" | "message">): string {
  if (req.conversationId) {
    return req.message
      ? `A Townsy moderator reviews this message with the recent messages of this conversation (both sides). ${req.label} is not told who reported them.`
      : `A Townsy moderator reviews it with the recent messages of this conversation (both sides). ${req.label} is not told who reported them.`;
  }
  return req.message
    ? `A Townsy moderator reviews this message with ${req.label}'s recent messages in this room. They are not told who reported them.`
    : `A Townsy moderator reviews it with ${req.label}'s recent messages in this room. They are not told who reported them.`;
}

/** The reported message, quoted as plain text (never a link, never HTML). */
export function ReportedMessageQuote({ label, message }: { label: string; message: NonNullable<PersonReportRequestUi["message"]> }) {
  return (
    <figure className="report-quote" data-testid="person-report-quote">
      <figcaption>
        <span className="report-quote-flag" aria-hidden="true">
          <ReportFlagIcon />
        </span>
        The message you are reporting
      </figcaption>
      <blockquote className="pre-wrap">{message.text}</blockquote>
      <div className="report-quote-meta">
        {label} · {formatDateTime(message.at)}
      </div>
    </figure>
  );
}

/**
 * "Report this person" (ADR-0013; docs/08 section 6): a category, an optional note, and the person's recent lines in this
 * room go to Townsy moderators as a protected snapshot. "Report this message" (a chat line or a private message) quotes
 * that message and sends its id: the server keeps it in the snapshot, marked, when it is that person's. Works for
 * guests and accounts (private messages: accounts).
 */
export function PersonReportDialog() {
  const req = useTownStore((s) => s.ui.personReport);
  const currentRoomKey = useTownStore((s) => s.room.roomKey);
  const [category, setCategory] = useState<PersonReportCategory | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!req) return null;
  const roomKey = req.roomKey ?? currentRoomKey;
  const S = useTownStore.getState;
  const close = () => {
    S().openPersonReport(null);
    setCategory(null);
    setNote("");
    setError(null);
  };
  const inConversation = Boolean(req.conversationId);
  const send = async () => {
    if (!category || (!roomKey && !inConversation)) return;
    setBusy(true);
    setError(null);
    try {
      const where = req.conversationId ? { conversationId: req.conversationId } : { roomKey: roomKey! };
      const res = await postPersonReport({
        targetActorId: req.actorId,
        ...where,
        ...(req.message ? { messageId: req.message.messageId } : {}),
        category,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      S().pushToast(reportSentText(req.label, res.duplicate, res.messageIncluded), "success");
      close();
    } catch (err) {
      setError(errorMessage(err, "The report could not be sent. Try again."));
    } finally {
      setBusy(false);
    }
  };
  const blocked = !roomKey && !inConversation ? "Join a room first." : !category ? "Choose what happened." : null;
  return (
    <Dialog
      title={req.message ? `Report a message from ${req.label}` : `Report ${req.label}`}
      onClose={close}
      footer={
        <>
          <button type="button" className="btn" onClick={close}>
            Cancel
          </button>
          <button type="button" className="btn btn-danger" disabled={busy || Boolean(blocked)} onClick={() => void send()} data-testid="person-report-send">
            {busy ? "Sending…" : "Send report"}
          </button>
        </>
      }
    >
      <div className="stack" data-testid="person-report-dialog" data-message-id={req.message?.messageId}>
        <p className="small muted" style={{ margin: 0 }}>
          {reportIntroText(req)}
          {isAiResidentActorId(req.actorId) ? " AI residents are checked by the Townsy team." : ""} If someone is in danger, contact local emergency services.
        </p>
        {req.message ? <ReportedMessageQuote label={req.label} message={req.message} /> : null}
        <fieldset className="choice-group report-categories">
          <legend>What happened?</legend>
          {PersonReportCategory.options.map((c) => (
            <label key={c} className="choice">
              <input type="radio" name="person-report-category" value={c} checked={category === c} onChange={() => setCategory(c)} data-testid={`person-report-${c}`} />
              <span>{PERSON_REPORT_CATEGORY_LABEL[c]}</span>
            </label>
          ))}
        </fieldset>
        <label className="field">
          <span className="small">Anything else the moderator should know? (optional)</span>
          <textarea rows={3} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} data-testid="person-report-note" />
          <span className="hint">{note.length}/500 · plain text</span>
        </label>
        {error ? (
          <div className="notice notice-danger small" role="alert">
            {error}
          </div>
        ) : !roomKey && !inConversation ? (
          <span className="hint">Join a room first.</span>
        ) : null}
      </div>
    </Dialog>
  );
}
